import { useCallback, useEffect, useState } from 'react'
import { Platform } from 'react-native'
import { activateKeepAwakeAsync, deactivateKeepAwake } from 'expo-keep-awake'
import { useScreenOrientation } from '@hooks/useScreenOrientation'
import type { IUpdateLessonProgress } from '@components/MediaRender'
import type { WebViewMessageEvent } from 'react-native-webview'

interface IVimeoVideoPlayerData {
  currentTime: number
  duration: number
  percent: number
  seconds?: number
}

interface IVimeoEventData {
  event:
    | 'timeupdate'
    | 'play'
    | 'pause'
    | 'enterpictureinpicture'
    | 'exitpictureinpicture'
  data: IVimeoVideoPlayerData
}

const PROGRESS_THRESHOLDS = [25, 50, 75, 100] as const

export interface IUseVimeoVideoPlayerParams {
  actualProgress?: number
  updateProgress: (p: IUpdateLessonProgress) => void
}

export function useVimeoVideoPlayer(params: IUseVimeoVideoPlayerParams) {
  const { actualProgress = 0, updateProgress } = params

  const [progressMarkers, setProgressMarkers] = useState({
    25: actualProgress >= 25,
    50: actualProgress >= 50,
    75: actualProgress >= 75,
    100: actualProgress >= 100,
  })
  const [isPlaying, setIsPlaying] = useState(false)
  const [isInFullscreen, setIsInFullscreen] = useState(false)

  const { unlockOrientation, lockToPortrait } = useScreenOrientation()

  const isAndroid = Platform.OS === 'android'

  useEffect(() => {
    if (isPlaying && isAndroid) {
      activateKeepAwakeAsync()
    } else {
      deactivateKeepAwake()
    }
    return () => {
      deactivateKeepAwake()
    }
  }, [isPlaying, isAndroid])

  useEffect(() => {
    if (isInFullscreen) {
      unlockOrientation()
    } else {
      lockToPortrait()
    }
  }, [isInFullscreen, unlockOrientation, lockToPortrait])

  const handleTimeUpdate = useCallback(
    (data: IVimeoVideoPlayerData) => {
      const percentValue = Number(data?.percent ?? 0)
      const percentNormalized = Math.floor(
        Number.isFinite(percentValue)
          ? percentValue <= 1
            ? percentValue * 100
            : percentValue
          : 0,
      )
      const seconds = (data as unknown as { seconds?: number })?.seconds
      const currentTimeSeconds = Number(
        typeof seconds === 'number' ? seconds : (data.currentTime ?? 0),
      )
      PROGRESS_THRESHOLDS.forEach((threshold) => {
        if (percentNormalized >= threshold && !progressMarkers[threshold]) {
          setProgressMarkers((prev) => ({ ...prev, [threshold]: true }))
          updateProgress?.({
            progress: threshold,
            currentVideoTime: Math.floor(currentTimeSeconds),
          })
        }
      })
    },
    [progressMarkers, updateProgress],
  )

  const handleMessage = useCallback(
    (event: WebViewMessageEvent) => {
      try {
        const data: IVimeoEventData = JSON.parse(event.nativeEvent.data)
        if (data.event === 'timeupdate') {
          handleTimeUpdate(data.data)
        } else if (data.event === 'play') {
          setIsPlaying(true)
        } else if (data.event === 'pause') {
          setIsPlaying(false)
        } else if (data.event === 'enterpictureinpicture') {
          setIsInFullscreen(true)
        } else if (data.event === 'exitpictureinpicture') {
          setIsInFullscreen(false)
        }
      } catch {
        // ignore
      }
    },
    [handleTimeUpdate],
  )

  const handleLoadStart = useCallback(() => {
    setIsInFullscreen(false)
  }, [])

  return {
    handleMessage,
    handleLoadStart,
  }
}
